// "Gratis verzending (NL)" → "gratis verzending (NL)" in a sentence: only the first letter goes lower case
const lowerFirst = (text) => {
  const value = String(text || '');
  return value.charAt(0).toLowerCase() + value.slice(1);
};

class CartDrawerController {
  constructor() {
    // see enqueue(): every change to the cart from this tab runs through here, one at a time
    this.queue = Promise.resolve();
    this.pending = 0;
    this.latestCart = null;
    this.renderedCart = null;
    this.pageCart = null;
    this.rewardsRun = null;
    this.syncingRewards = false;
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    // zelfde notatie als Liquid's | money (bv. €{{amount_with_comma_separator}} → €6,95)
    this.moneyFormat = this.drawer.dataset.moneyFormat || '';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';
    this.supplierDeliveryLabel = this.drawer.dataset.cartDrawerSupplierDeliveryLabel || '';
    // artikelen die nog niet op voorraad liggen: zichtbare eigenschap met de zin, verborgen _verzending_rond met de datum
    this.deliveryProperty = this.drawer.dataset.cartDrawerDeliveryProperty || '';
    this.shipsNoticeTemplate = this.drawer.dataset.cartDrawerShipsNotice || '';
    this.shipsBetweenNoticeTemplate = this.drawer.dataset.cartDrawerShipsBetweenNotice || '';
    this.shipsNotice = this.drawer.querySelector('[data-cart-drawer-ships]');
    this.extras = this.drawer.querySelector('[data-cart-drawer-extras]');
    // Vanaf tablet opent het winkelwagen-icoon de winkelwagenpagina; op de telefoon de lade (die daar al schermvullend is)
    this.pageMedia = window.matchMedia('(min-width: 750px)');

    this.bindUI();
  }

  bindUI() {
    const syncToggleRole = () => {
      this.toggleNodes.forEach((trigger) => {
        if (this.pageMedia.matches) {
          trigger.removeAttribute('aria-haspopup');
          trigger.removeAttribute('aria-expanded');
        } else {
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    };
    syncToggleRole();
    this.pageMedia.addEventListener?.('change', syncToggleRole);

    this.toggleNodes.forEach((trigger) => {
      trigger.addEventListener('click', async (event) => {
        if (this.pageMedia.matches) return; // tablet/laptop/desktop: naar de winkelwagenpagina
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    // Capture-fase op window, met stopPropagation in handleAddToCart: Shopify's web pixel manager luistert verderop ook
    // naar deze submit en meldde dan product_added_to_cart, naast de melding voor de fetch naar cart/add.js. Meta telde
    // zo elke toevoeging dubbel (29-10); nu meldt Shopify alleen de fetch.
    window.addEventListener('submit', (event) => this.handleAddToCart(event), true);

    // To the checkout only once the cart queue is empty: a gift that is still being added or removed is then in order
    // first, instead of the checkout getting the cart as it was a moment ago
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-cart-checkout]') : null;
        const page = window.cartPage;
        if (!button || (!this.pending && !page?.refreshing)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // the cart page form posts its quantities along, so it waits for the redrawn page (and its own new button)
        const onPage = Boolean(button.closest('[data-cart-page]'));
        this.idle()
          .then(() => page?.refreshing)
          .then(() => (onPage ? document.querySelector('[data-cart-page] [data-cart-checkout]') : button)?.click());
      },
      true
    );
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData(form);
    // per add (not on this): with quick taps on several product tiles every button gets its own label back
    const addState = { button: submitter, label: submitter.querySelector('[data-add-to-cart-label]') };
    addState.text = addState.label?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(addState, true);

      const response = await this.enqueue(() => fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      }));

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
      // more than in stock: Shopify adds what there is and still answers with an error, so the count and gifts follow
      this.refresh().catch(() => {});
    } finally {
      this.setAddButtonState(addState, false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }

    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;
      if (item.classList.contains('cart-drawer__item--reward') && item.dataset.cartItemVariant) {
        this.declineReward(item.dataset.cartItemVariant);
      }

      await this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    let cart = null;
    try {
      cart = await this.enqueue(() => fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      }).then((response) => {
        if (!response.ok) {
          throw new Error('Unable to update cart item');
        }
        return response.json();
      }));
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }

    if (cart) {
      await this.show(cart).catch((error) => console.error(error));
    } else {
      await this.refresh();
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) {
      this.showSkeleton(Math.min(currentCount, 3));
    }

    try {
      const cart = await this.enqueue(() => this.fetchCart());
      this.show(cart).catch((error) => console.error(error));
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  fetchCart() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Unable to load the cart');
      return response.json();
    });
  }

  // ----- Cart queue: every change to the cart from this tab (quantities, removals, adds, gift lines, note, terms) runs
  // one at a time, each starting from the cart the previous one left. Shopify can lose one of two changes that arrive at
  // the same time, and a quick second click could put an older cart on screen or leave a gift behind; one at a time,
  // neither can happen. A request that hangs holds the queue for 15 s at most.
  enqueue(task) {
    const run = this.queue.then(task);
    this.pending += 1;
    this.queue = this.queue
      .then(() => Promise.race([run, new Promise((resolve) => setTimeout(resolve, 15000))]))
      .catch(() => {})
      .then(() => {
        this.pending -= 1;
      });
    return run;
  }

  // Resolves once the queue is empty
  idle() {
    return this.pending ? this.queue.then(() => this.idle()) : Promise.resolve();
  }

  // After every change: show the cart right away, then let the gift lines follow the ladder and show it again if that
  // changed anything. On the cart page the page follows as well, so the drawer and the page never disagree.
  async show(cart, { paint = true } = {}) {
    this.latestCart = cart;
    if (paint) this.render(cart);
    const synced = await this.syncRewards().catch((error) => {
      console.error(error);
      return this.latestCart;
    });
    if (synced !== this.latestCart) return synced; // a newer cart came in meanwhile: its own show() takes over
    if (synced !== this.renderedCart) this.render(synced);
    const page = window.cartPage;
    if (page?.section && synced !== this.pageCart) {
      this.pageCart = synced;
      await page.refresh(synced);
    }
    return synced;
  }

  // One look at the live cart on page load: fills the cross-sell row, and the gift lines follow the ladder even when the
  // cart changed elsewhere (checkout, another tab). Liquid already rendered this cart, so the drawer and the cart page are
  // only redrawn when the gift lines had to change.
  async load() {
    const cart = await this.enqueue(() => this.fetchCart());
    window.cartCrossSell?.update(cart);
    this.renderedCart = cart;
    this.pageCart = cart;
    if (cart.item_count) await this.show(cart, { paint: false });
  }

  // ----- Reward ladder (free gift / free shipping / free booster) -----
  rewardsConfig(node) {
    try { return JSON.parse(node.dataset.tiers || '[]'); } catch (error) { return []; }
  }

  isRewardItem(item, tiers) {
    if (item.properties && item.properties._reward) return true;
    return tiers.some((tier) => tier.variantId && String(item.variant_id) === String(tier.variantId));
  }

  paidSubtotal(cart, tiers) {
    return (cart.items || []).reduce((sum, item) => (this.isRewardItem(item, tiers) ? sum : sum + (item.final_line_price || 0)), 0);
  }

  renderRewards(cart) {
    document.querySelectorAll('[data-cart-rewards]').forEach((node) => {
      const tiers = this.rewardsConfig(node);
      if (!tiers.length) return;
      const itemCount = (cart.items || []).filter((item) => !this.isRewardItem(item, tiers)).length;
      node.hidden = itemCount === 0;
      if (!itemCount) return;

      const subtotal = this.paidSubtotal(cart, tiers);
      const next = tiers.find((tier) => subtotal < tier.amount);
      node.classList.toggle('is-complete', !next);

      const text = node.querySelector('[data-cart-rewards-text] span');
      if (text) {
        if (next) {
          text.innerHTML = node.dataset.templateRemaining
            .replace('[[amount]]', this.escapeHtml(this.formatMoney(next.amount - subtotal)))
            .replace('[[label]]', this.escapeHtml(lowerFirst(next.label)));
        } else {
          text.textContent = node.dataset.labelAll;
        }
      }
      const icon = node.querySelector('[data-cart-rewards-text] svg');
      if (icon) icon.style.color = next ? '' : '#2f9e5b';

      const steps = node.querySelectorAll('.cart-rewards__step');
      let previous = 0;
      tiers.forEach((tier, index) => {
        const step = steps[index];
        if (!step) return;
        const span = Math.max(tier.amount - previous, 1);
        const segment = Math.max(0, Math.min(100, Math.round(((subtotal - previous) / span) * 100)));
        const fill = step.querySelector('[data-cart-rewards-segment]');
        const track = step.querySelector('[role="progressbar"]');
        if (fill) fill.style.width = segment + '%';
        if (track) track.setAttribute('aria-valuenow', String(segment));
        step.classList.toggle('is-reached', subtotal >= tier.amount);
        previous = tier.amount;
      });
    });
  }

  // Gift lines follow the ladder (see applyRewards). Through the queue, so it always works on the newest cart; a check
  // that has not started yet covers every change before it, so one is enough.
  syncRewards() {
    if (!this.rewardsRun) {
      this.rewardsRun = this.enqueue(async () => {
        this.rewardsRun = null;
        this.syncingRewards = true;
        try {
          const before = this.latestCart;
          const after = await this.applyRewards(before);
          if (this.latestCart === before) this.latestCart = after;
          return this.latestCart;
        } finally {
          this.syncingRewards = false;
        }
      });
    }
    return this.rewardsRun;
  }

  // Per gift exactly one line of 1 from its tier on (unless the customer removed it on purpose), none below it. Extra
  // lines go, and a gift added by hand (its own product page) gets the _reward property, so Liquid and this script see the
  // same gift line. Returns the cart as it is afterwards: the same object when nothing had to change.
  async applyRewards(cart) {
    const node = document.querySelector('[data-cart-rewards]');
    if (!node || !cart) return cart;
    const allTiers = this.rewardsConfig(node);
    const tiers = allTiers.filter((tier) => tier.variantId);
    if (!tiers.length) return cart;

    const subtotal = this.paidSubtotal(cart, allTiers);
    const updates = {};
    const relabel = [];
    const adds = [];
    tiers.forEach((tier) => {
      const lines = (cart.items || []).filter((item) => String(item.variant_id) === String(tier.variantId));
      const keep = subtotal >= tier.amount ? lines.find((item) => item.properties?._reward) || lines[0] : null;
      lines.forEach((item) => {
        if (item === keep && !item.properties?._reward) relabel.push({ id: item.key, quantity: 1, properties: { _reward: tier.label } });
        else if (item.quantity !== (item === keep ? 1 : 0)) updates[item.key] = item === keep ? 1 : 0;
      });
      if (subtotal >= tier.amount && !lines.length && !this.rewardDeclined(tier.variantId)) {
        adds.push({ id: Number(tier.variantId), quantity: 1, properties: { _reward: tier.label } });
      }
    });
    if (!Object.keys(updates).length && !relabel.length && !adds.length) return cart;

    const root = window.Shopify?.routes?.root || '/';
    const post = (url, body) => fetch(root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    let current = cart;
    try {
      if (Object.keys(updates).length) {
        const response = await post('cart/update.js', { updates });
        current = response.ok ? await response.json() : await this.fetchCart();
      }
      for (const line of relabel) {
        const response = await post('cart/change.js', line);
        current = response.ok ? await response.json() : await this.fetchCart();
      }
      if (adds.length) {
        await post('cart/add.js', { items: adds });
        current = await this.fetchCart();
      }
    } catch (error) {
      console.error(error);
      current = await this.fetchCart().catch(() => current);
    }
    return current;
  }

  rewardDeclined(variantId) {
    try { return sessionStorage.getItem('ember-reward-declined-' + variantId) === '1'; } catch (error) { return false; }
  }

  declineReward(variantId) {
    try { sessionStorage.setItem('ember-reward-declined-' + variantId, '1'); } catch (error) { /* private mode */ }
  }

  // Melding boven de artikelen als de bestelling iets bevat dat nog niet op voorraad ligt: de laatste datum
  // (dan is alles compleet), want we verzenden in één keer. Hoort die bij een periode (_verzending_van), dan
  // "tussen <van> en <tot>".
  renderShipsNotice(cart) {
    if (!this.shipsNotice) return;
    let last = '';
    let first = '';
    for (const item of cart.items || []) {
      const rond = item.properties?._verzending_rond;
      if (rond && rond > last) {
        last = rond;
        first = item.properties?._verzending_van || '';
      }
    }
    if (!last || !this.shipsNoticeTemplate) {
      this.shipsNotice.hidden = true;
      this.shipsNotice.textContent = '';
      return;
    }
    const lang = document.documentElement.lang || 'nl';
    const format = (date, withYear) => {
      try {
        return new Date(`${date}T12:00:00`).toLocaleDateString(lang, withYear ? { day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'long' });
      } catch (e) { return date; /* onbekende datum: dan de ruwe waarde */ }
    };
    if (first && this.shipsBetweenNoticeTemplate) {
      const sameYear = first.slice(0, 4) === last.slice(0, 4);
      this.shipsNotice.textContent = this.shipsBetweenNoticeTemplate.replace('[[from]]', format(first, !sameYear)).replace('[[until]]', format(last, true));
    } else {
      this.shipsNotice.textContent = this.shipsNoticeTemplate.replace('[[date]]', format(last, true));
    }
    this.shipsNotice.hidden = false;
  }

  render(cart) {
    this.renderedCart = cart;
    this.hydrateCompareAtPrices(cart);
    this.renderShipsNotice(cart);
    // Kadoregels tellen niet mee (de klant heeft ze niet zelf gekozen)
    const itemCount = (cart.items || []).reduce((sum, item) => (item.properties && item.properties._reward ? sum : sum + (item.quantity || 0)), 0);

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    const compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + (item.compare_at_line_price || item.final_line_price || 0),
      0
    );
    if (this.compareSubtotalNode) {
      const showCompareSubtotal = this.showCompareAt && compareTotal > (cart.total_price || 0);
      this.compareSubtotalNode.hidden = !showCompareSubtotal;
      this.compareSubtotalNode.textContent = showCompareSubtotal ? this.formatMoney(compareTotal) : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }
    if (this.extras) {
      this.extras.hidden = itemCount === 0;
    }

    this.renderRewards(cart);
    window.cartCrossSell?.update(cart);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="button button--primary cart-drawer__empty-cta">
            ${this.escapeHtml(this.continueLabel)}
          </a>
        </div>
      `;
      return;
    }

    // gift lines last (as in Liquid): a gift that comes or goes then never moves the line the customer is about to tap
    const rewardsNodeForOrder = document.querySelector('[data-cart-rewards]');
    const rewardTiers = rewardsNodeForOrder ? this.rewardsConfig(rewardsNodeForOrder) : [];
    const itemsMarkup = [...cart.items]
      .sort((a, b) => Number(this.isRewardItem(a, rewardTiers)) - Number(this.isRewardItem(b, rewardTiers)))
      .map((item) => {
        const imageMarkup = item.image
          ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
              <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const variantMarkup =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
            : '';

        // supplier products (hidden property _leverancier, see snippets/supplier-line-property.liquid) show their delivery time
        const deliveryMarkup =
          item.properties?._leverancier && this.supplierDeliveryLabel
            ? `<p class="cart-drawer__item-variant cart-drawer__item-delivery">${this.escapeHtml(this.supplierDeliveryLabel)}</p>`
            : '';

        // nog niet op voorraad: "Wordt verzonden rond <datum>" (zin komt uit de zichtbare regel-eigenschap)
        const shipsText = this.deliveryProperty ? item.properties?.[this.deliveryProperty] : '';
        const shipsMarkup = shipsText
          ? `<p class="cart-drawer__item-variant cart-drawer__item-delivery">${this.escapeHtml(shipsText)}</p>`
          : '';

        const comparePriceMarkup =
          this.showCompareAt && (item.compare_at_line_price || 0) > (item.final_line_price || 0)
            ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(item.compare_at_line_price)}</p>`
            : '';

        const isPending = this.pendingItemKeys.has(item.key);
        const rewardsNode = document.querySelector('[data-cart-rewards]');
        const isReward = rewardsNode ? this.isRewardItem(item, this.rewardsConfig(rewardsNode)) : false;
        const freeLabel = rewardsNode ? rewardsNode.dataset.labelFree : '';
        const orderDiscounted = isReward && (cart.cart_level_discount_applications || []).some((d) => String(d.title || '').toLowerCase().includes(String(item.properties?._reward || '').toLowerCase()));
        const priceMarkup = isReward && ((item.final_line_price || 0) === 0 || orderDiscounted)
          ? `<p class="cart-drawer__item-price cart-drawer__item-price--free">${this.escapeHtml(freeLabel)}</p>`
          : `<p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>`;
        const rewardName = item.properties?._reward || freeLabel;
        const rewardTag = isReward && rewardName.toLowerCase() !== String(item.product_title || '').toLowerCase() ? `<p class="cart-drawer__item-reward">${this.escapeHtml(rewardName)}</p>` : '';
        const quantityMarkup = isReward
          ? ''
          : `<div class="cart-drawer__quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>-</button>
                  <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${isPending ? 'disabled' : ''}>
                  <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${isPending ? 'disabled' : ''}>+</button>
                </div>`;

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}${isReward ? ' cart-drawer__item--reward' : ''}" data-cart-item-key="${item.key}" data-cart-item-variant="${item.variant_id}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <div class="cart-drawer__item-top">
                <div>
                  ${rewardTag}
                  <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                  ${variantMarkup}
                  ${deliveryMarkup}
                  ${shipsMarkup}
                </div>
                <div class="cart-drawer__item-prices">
                  ${priceMarkup}
                  ${isReward ? '' : comparePriceMarkup}
                </div>
              </div>
              <div class="cart-drawer__item-bottom">
                ${quantityMarkup}
                <button type="button" class="cart-drawer__remove" data-cart-remove ${isPending ? 'disabled' : ''} aria-label="${this.escapeHtml(this.removeLabel)}: ${this.escapeHtml(item.product_title)}">${this.escapeHtml(this.removeLabel)}</button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(state, isLoading) {
    if (!state?.button) return;

    state.button.disabled = isLoading;
    if (state.label) {
      state.label.textContent = isLoading ? this.addingLabel : state.text;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    const value = Number(cents || 0);
    const match = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.currency }).format(value / 100);
    }
    const group = (number, decimals, thousands, decimal) => {
      const [whole, fraction] = (number / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const formats = {
      amount: () => group(value, 2, ',', '.'),
      amount_no_decimals: () => group(value, 0, ',', '.'),
      amount_with_comma_separator: () => group(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(value, 0, '.', ','),
      amount_with_space_separator: () => group(value, 2, ' ', ','),
      amount_with_apostrophe_separator: () => group(value, 2, "'", '.')
    };
    const formatted = (formats[match[1]] || formats.amount)();
    return this.moneyFormat.replace(match[0], formatted).replace(/<[^>]*>/g, '');
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

// ----- Terms checkbox before checkout (snippet cart-terms): the checkout button stays locked until ticked; the tick is
// stored as cart attribute "Voorwaarden geaccepteerd" so it lands on the order. Works in the drawer and on the cart page.
class CartTerms {
  constructor() {
    if (!document.querySelector('[data-cart-terms]')) return;
    this.root = window.Shopify?.routes?.root || '/';
    document.addEventListener('change', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-cart-terms-checkbox]')) this.setAccepted(event.target.checked);
    });
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-cart-checkout]') : null;
        if (!button || this.accepted()) return;
        event.preventDefault();
        event.stopPropagation();
        this.warn(button);
      },
      true
    );
    this.sync();
  }

  accepted() {
    return [...document.querySelectorAll('[data-cart-terms-checkbox]')].some((box) => box.checked);
  }

  sync() {
    const accepted = this.accepted();
    document.querySelectorAll('[data-cart-terms-checkbox]').forEach((box) => { box.checked = accepted; });
    document.querySelectorAll('[data-cart-checkout]').forEach((button) => {
      button.classList.toggle('is-locked', !accepted);
      button.setAttribute('aria-disabled', accepted ? 'false' : 'true');
    });
    if (accepted) document.querySelectorAll('[data-cart-terms-hint]').forEach((hint) => { hint.hidden = true; });
  }

  async setAccepted(accepted) {
    // Both places (drawer + cart page) share one state: the box that changed wins.
    document.querySelectorAll('[data-cart-terms-checkbox]').forEach((box) => { box.checked = accepted; });
    this.sync();
    const stamp = accepted
      ? new Date().toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '';
    try {
      const drawer = window.cartDrawer;
      const send = () => fetch(this.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Voorwaarden geaccepteerd': stamp } })
      });
      await (drawer?.drawer ? drawer.enqueue(send) : send());
    } catch (error) {
      // The tick itself still gates the button; the attribute is the record on the order.
    }
  }

  warn(button) {
    const scope = button.closest('[data-cart-drawer-footer], form') || document;
    const hint = scope.querySelector('[data-cart-terms-hint]') || document.querySelector('[data-cart-terms-hint]');
    const box = scope.querySelector('[data-cart-terms-checkbox]') || document.querySelector('[data-cart-terms-checkbox]');
    if (hint) hint.hidden = false;
    if (!box) return;
    const wrap = box.closest('[data-cart-terms]');
    if (wrap) {
      wrap.classList.remove('is-shaking');
      void wrap.offsetWidth;
      wrap.classList.add('is-shaking');
    }
    box.focus();
  }
}

// ----- Order note (snippet cart-note): collapsed behind "Opmerking toevoegen"; saved automatically as the cart note,
// which lands on the order. The drawer and the cart page mirror each other.
class CartNote {
  constructor() {
    this.root = window.Shopify?.routes?.root || '/';
    this.timer = null;
    this.statusTimer = null;
    const first = document.querySelector('[data-cart-note-input]');
    this.saved = first ? first.value : '';

    document.addEventListener('input', (event) => {
      const input = event.target instanceof Element ? event.target.closest('[data-cart-note-input]') : null;
      if (input) this.onInput(input);
    });
    document.addEventListener('focusout', (event) => {
      const input = event.target instanceof Element ? event.target.closest('[data-cart-note-input]') : null;
      if (input) this.save(input.value);
    });
    // Opening the note puts the cursor straight in the text field ('toggle' does not bubble, so listen while capturing)
    document.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-cart-note]') || !details.open) return;
        const input = details.querySelector('[data-cart-note-input]');
        if (!input) return;
        this.autosize(input);
        input.focus({ preventScroll: true });
        const end = input.value.length;
        input.setSelectionRange?.(end, end);
      },
      true
    );
  }

  onInput(input) {
    const value = input.value;
    document.querySelectorAll('[data-cart-note-input]').forEach((other) => {
      if (other !== input) other.value = value;
    });
    this.updatePreviews(value);
    this.autosize(input);
    this.setStatus('saving');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(value), 700);
  }

  updatePreviews(value) {
    const text = value.trim();
    document.querySelectorAll('[data-cart-note]').forEach((details) => {
      details.toggleAttribute('data-has-note', text !== '');
      const preview = details.querySelector('[data-cart-note-preview]');
      if (preview) preview.textContent = text;
    });
  }

  autosize(input) {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight + 2, 132)}px`;
  }

  async save(value) {
    clearTimeout(this.timer);
    if (value === this.saved) {
      this.setStatus('');
      return;
    }
    try {
      const drawer = window.cartDrawer;
      const send = () => fetch(this.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      });
      const response = await (drawer?.drawer ? drawer.enqueue(send) : send());
      if (!response.ok) throw new Error('Unable to save the order note');
      this.saved = value;
      this.setStatus('saved');
    } catch (error) {
      console.error(error);
      this.setStatus('');
    }
  }

  setStatus(state) {
    document.querySelectorAll('[data-cart-note]').forEach((details) => {
      const status = details.querySelector('[data-cart-note-status]');
      if (!status) return;
      const labels = { saved: details.dataset.labelSaved, saving: details.dataset.labelSaving };
      status.textContent = labels[state] || '';
      status.dataset.state = state;
    });
    clearTimeout(this.statusTimer);
    if (state === 'saved') this.statusTimer = setTimeout(() => this.setStatus(''), 2000);
  }
}

// ----- Cross-sell (snippet cart-cross-sell): a compact, swipeable row under the cart items. Only products that are in
// stock and not in the cart yet; the ones that close the gap to the next reward come first, marked "+ gratis cadeau".
class CartCrossSell {
  constructor() {
    this.root = window.Shopify?.routes?.root || '/';
    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-cart-cross-sell-add]') : null;
      if (button) this.add(button);
    });
  }

  products(node) {
    if (!node.crossSellProducts) {
      try {
        node.crossSellProducts = JSON.parse(node.querySelector('[data-cart-cross-sell-products]')?.textContent || '[]');
      } catch (error) {
        node.crossSellProducts = [];
      }
    }
    return node.crossSellProducts;
  }

  nextReward(cart) {
    const drawer = window.cartDrawer;
    const rewardsNode = document.querySelector('[data-cart-rewards]');
    if (!drawer?.drawer || !rewardsNode) return null;
    const tiers = drawer.rewardsConfig(rewardsNode);
    const subtotal = drawer.paidSubtotal(cart, tiers);
    const next = tiers.find((tier) => subtotal < tier.amount);
    return next ? { gap: next.amount - subtotal, label: String(next.label || '') } : null;
  }

  update(cart) {
    if (!cart) return;
    document.querySelectorAll('[data-cart-cross-sell]').forEach((node) => this.renderNode(node, cart));
  }

  renderNode(node, cart) {
    const track = node.querySelector('[data-cart-cross-sell-track]');
    if (!track) return;

    const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
    const hasItems = (cart.items || []).some((item) => !(item.properties && item.properties._reward));
    let list = this.products(node).filter((product) => !inCart.has(String(product.productId)));
    const next = hasItems ? this.nextReward(cart) : null;
    if (next) {
      const closers = list.filter((product) => product.price >= next.gap).sort((a, b) => a.price - b.price);
      const rest = list.filter((product) => product.price < next.gap);
      list = closers.concat(rest);
    }
    list = list.slice(0, 8);

    node.hidden = !hasItems || list.length === 0;
    if (node.hidden) {
      track.innerHTML = '';
      return;
    }

    const drawer = window.cartDrawer;
    const esc = (value) => (drawer?.escapeHtml ? drawer.escapeHtml(value) : String(value || ''));
    const money = (cents) => (drawer?.formatMoney ? drawer.formatMoney(cents) : (cents / 100).toFixed(2));
    const icon = node.querySelector('[data-cart-cross-sell-icon]')?.innerHTML || '+';
    const addLabel = node.dataset.labelAdd || '';
    const unlocksTemplate = node.dataset.labelUnlocks || '';

    track.innerHTML = list
      .map((product) => {
        const unlocks =
          next && product.price >= next.gap && unlocksTemplate
            ? `<span class="cart-cross-sell__unlocks">${esc(unlocksTemplate.replace('[[reward]]', lowerFirst(next.label)))}</span>`
            : '';
        const image = product.image ? `<img src="${esc(product.image)}" alt="" width="80" height="80" loading="lazy">` : '';
        return `
          <li class="cart-cross-sell__item">
            <a class="cart-cross-sell__media" href="${esc(product.url)}" tabindex="-1" aria-hidden="true">${image}</a>
            <div class="cart-cross-sell__info">
              <a class="cart-cross-sell__title" href="${esc(product.url)}">${esc(product.title)}</a>
              <p class="cart-cross-sell__meta"><span class="cart-cross-sell__price">${esc(money(product.price))}</span>${unlocks}</p>
            </div>
            <button type="button" class="cart-cross-sell__add" data-cart-cross-sell-add data-variant-id="${Number(product.variantId)}" aria-label="${esc(addLabel)}: ${esc(product.title)}">${icon}</button>
          </li>`;
      })
      .join('');
  }

  async add(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const drawer = window.cartDrawer;
      const send = () => fetch(this.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
      });
      const response = await (drawer?.drawer ? drawer.enqueue(send) : send());
      if (!response.ok) throw new Error('Unable to add the cross-sell product');
      if (button.closest('[data-cart-page]') && window.cartPage?.section) {
        await window.cartPage.reload();
      } else {
        await window.cartDrawer?.refresh();
      }
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

// ----- Cart page (sections/cart.liquid): quantities and removals without a reload, rewards kept in sync exactly like
// in the drawer, and the shipping estimate (NL/BE) with the total. The section is re-rendered via the Section
// Rendering API, so Liquid stays the only source of the markup.
class CartPage {
  constructor() {
    this.section = document.querySelector('[data-cart-page]');
    if (!this.section) return;
    this.root = window.Shopify?.routes?.root || '/';
    this.sectionId = this.section.dataset.sectionId;
    this.busy = false;
    this.section.addEventListener('click', (event) => this.onClick(event));
    this.section.addEventListener('change', (event) => this.onChange(event));
    this.applyShipping();
  }

  onClick(event) {
    const step = event.target.closest('[data-cart-line-step]');
    if (step) {
      const line = step.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-line-qty]');
      if (!line || !input) return;
      event.preventDefault();
      const next = Math.max(0, (Number(input.value) || 0) + Number(step.dataset.cartLineStep || 0));
      this.change(line.dataset.key, next);
      return;
    }

    const remove = event.target.closest('[data-cart-line-remove]');
    if (remove) {
      const line = remove.closest('[data-cart-line]');
      if (!line) return;
      event.preventDefault();
      if (line.hasAttribute('data-reward') && line.dataset.variant) window.cartDrawer?.declineReward(line.dataset.variant);
      this.change(line.dataset.key, 0);
      return;
    }

    const country = event.target.closest('[data-shipping-country]');
    if (country) {
      event.preventDefault();
      this.setCountry(country.dataset.shippingCountry);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-cart-line-qty]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    if (line) this.change(line.dataset.key, Math.max(0, Number(input.value) || 0));
  }

  async change(key, quantity) {
    if (!key || this.busy) return;
    this.busy = true;
    this.section.classList.add('is-updating');
    try {
      const drawer = window.cartDrawer;
      const send = () => fetch(this.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to update the cart');
        return response.json();
      });
      await this.afterCartChange(await (drawer?.drawer ? drawer.enqueue(send) : send()));
    } catch (error) {
      console.error(error);
      window.location.reload();
    } finally {
      this.busy = false;
      this.section.classList.remove('is-updating');
    }
  }

  // After an add from the cross-sell row on this page
  async reload() {
    const drawer = window.cartDrawer;
    const load = () => fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    await this.afterCartChange(await (drawer?.drawer ? drawer.enqueue(load) : load()));
  }

  async afterCartChange(cart) {
    const drawer = window.cartDrawer;
    if (drawer?.drawer) {
      await drawer.show(cart); // header count, the (closed) drawer, the gift lines and then this page
    } else {
      await this.refresh(cart);
    }
  }

  // While this runs, a click on checkout waits for it (see CartDrawerController.bindUI): the form posts the quantities
  // it shows, so they must be those of the current cart
  refresh(cart) {
    const run = this.redraw(cart);
    this.refreshing = run;
    const done = () => {
      if (this.refreshing === run) this.refreshing = null;
    };
    run.then(done, done);
    return run;
  }

  async redraw(cart) {
    // only the newest refresh lands: an older answer may come in later and would show an older cart
    const ticket = (this.refreshTicket = (this.refreshTicket || 0) + 1);
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`);
    const html = await response.text();
    if (ticket !== this.refreshTicket) return;

    // keep a half-typed note and whether it was open (read now, so nothing typed while loading gets lost)
    const note = this.section.querySelector('[data-cart-note]');
    const noteState = note ? { open: note.open, value: note.querySelector('[data-cart-note-input]')?.value } : null;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
    if (!fresh) {
      window.location.reload();
      return;
    }
    this.section.innerHTML = fresh.innerHTML;

    const newNote = this.section.querySelector('[data-cart-note]');
    const input = newNote?.querySelector('[data-cart-note-input]');
    if (noteState && newNote && input && typeof noteState.value === 'string') {
      input.value = noteState.value;
      newNote.open = noteState.open;
      newNote.toggleAttribute('data-has-note', noteState.value.trim() !== '');
      const preview = newNote.querySelector('[data-cart-note-preview]');
      if (preview) preview.textContent = noteState.value.trim();
    }

    window.cartTerms?.sync();
    this.applyShipping();
    window.cartCrossSell?.update(cart);
  }

  // ----- Shipping estimate: a flat rate per country, free from the threshold in the free-shipping countries -----
  storedCountry() {
    try {
      return localStorage.getItem('ember-ship-country');
    } catch (error) {
      return null;
    }
  }

  setCountry(code) {
    try {
      localStorage.setItem('ember-ship-country', code);
    } catch (error) {
      /* private mode: the choice just isn't remembered */
    }
    this.applyShipping();
  }

  applyShipping() {
    const box = this.section.querySelector('[data-cart-shipping]');
    if (!box) return;
    const stored = this.storedCountry();
    const code = ['NL', 'BE'].includes(stored) ? stored : box.dataset.defaultCountry || 'NL';
    const rate = Number(code === 'BE' ? box.dataset.rateBe : box.dataset.rateNl) || 0;
    const freeFrom = Number(box.dataset.freeFrom || 0);
    const freeCountries = String(box.dataset.freeCountries || 'NL').toUpperCase().split(',').map((c) => c.trim());
    const freeHere = freeFrom > 0 && freeCountries.includes(code);
    const subtotal = Number(box.dataset.subtotal || 0);
    const free = freeHere && subtotal >= freeFrom;
    const money = (cents) => (window.cartDrawer?.formatMoney ? window.cartDrawer.formatMoney(cents) : (cents / 100).toFixed(2));

    box.querySelectorAll('[data-shipping-country]').forEach((button) => {
      button.setAttribute('aria-checked', button.dataset.shippingCountry === code ? 'true' : 'false');
    });
    const amount = box.querySelector('[data-shipping-amount]');
    if (amount) {
      amount.textContent = free ? box.dataset.labelFree || '' : money(rate);
      amount.classList.toggle('is-free', free);
    }
    const hint = box.querySelector('[data-shipping-hint]');
    if (hint) hint.hidden = free || !freeHere;
    const total = this.section.querySelector('[data-cart-total]');
    if (total) total.textContent = money(subtotal + (free ? 0 : rate));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawerController();
  window.cartTerms = new CartTerms();
  window.cartNote = new CartNote();
  window.cartCrossSell = new CartCrossSell();
  window.cartPage = new CartPage();

  // One look at the live cart on load (cross-sell row, gift lines): see CartDrawerController.load()
  if (!window.cartDrawer.drawer) return;
  window.cartDrawer.load().catch(() => {});
});
